/**
 * @file 1 つのステップ (学年) の「面」— タブと視覚的に接続した 1 枚のパネル。
 *
 * @what: 選んだステップの中身を 3 つの小節 (説明 → 用意するもの → 単元と取り組み) として
 *   1 枚の面に描く。小節は独立カードにせず破線の罫線 + h4 見出しで区切り、
 *   「同じ面の中の横並び要素」であることを見た目で言う (独立セクションは route 側の h2)。
 *   単元は領域 (9) ごとのカード、状態ボタンで 未着手 → 取り組み中 → できた、
 *   「このステップをいまここにする」で現在のステップを変えられる。
 * @why: パーツの見た目が均質だと「同じセクションの横並びか独立セクションか」の情報構造が
 *   読めない (代表 2026-08-20)。タブと面の接続 (選択タブが面と一体化) は study.css の
 *   .study-steps / .study-step-tab[aria-selected] / .study-step-surface の 3 点で成立して
 *   いるので、スタイル変更時はセットで扱うこと。
 * @failure: 描画のみ。編集系 props が無いときは読み取り専用で描く。
 */

import { useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  STUDY_MATERIAL_STATUS_LABELS,
  STUDY_UNIT_STATUS_LABELS,
  type StudyMaterialStatus,
  type StudyUnitStatus,
} from "../api/plans";
import { levelById, unitsInCell, type AttainmentModel, type Stage } from "../model";
import { entrySummary, type CorpusEntry } from "../model/corpusIndex";
import { domainColor } from "./domainColors";
import { FocusList } from "./FocusList";
import { ASSESSMENT_ICONS, DOMAIN_ICONS } from "./studyIcons";
import "./study.css";

/** 単元行を開いたときに並べる出題の数。これを超えた分はリーダーで続きを読む。 */
const UNIT_CORPUS_PREVIEW = 5;

export interface StepPanelProps {
  model: AttainmentModel;
  stage: Stage;
  /** 選択中のお子さんの単元状態。編集不可のときは undefined。 */
  unitStates?: Record<string, { status: StudyUnitStatus; note?: string }>;
  onToggleUnit?: (unitId: string) => void;
  isCurrent: boolean;
  /** 指定されると「このステップをいまここにする」ボタンを出す。 */
  onMakeCurrent?: () => void;
  /** ステップの説明と領域カードの間に挟むもの (用意するもの・教材)。 */
  preparationSlot?: ReactNode;
  /** unit id → その単元に紐づいた教材 (チップ表示用)。 */
  materialsByUnit?: Record<string, { title: string; status: StudyMaterialStatus }[]>;
  /** unit id → 「計画のもと」に載っている出題。単元行をその場で開いて実物を確かめる。 */
  corpusByUnit?: Record<string, CorpusEntry[]>;
  /** リーダーへのリンクの基点 (`/study/{familyId}/sources`)。無ければリンクを出さない。 */
  sourceHrefBase?: string;
}

export function StepPanel({
  model,
  stage,
  unitStates,
  onToggleUnit,
  isCurrent,
  onMakeCurrent,
  preparationSlot,
  materialsByUnit,
  corpusByUnit,
  sourceHrefBase,
}: StepPanelProps) {
  const editable = onToggleUnit !== undefined;
  const [openUnits, setOpenUnits] = useState<ReadonlySet<string>>(new Set());
  const toggleOpen = (unitId: string) =>
    setOpenUnits((prev) => {
      const next = new Set(prev);
      if (!next.delete(unitId)) next.add(unitId);
      return next;
    });
  const checkpoints = model.calibrationPoints.filter((c) => c.stage === stage.id);
  // このステップの単元がどの形式の考査につながるか (ペーパー偏重を一目で見るための行)
  const assessmentCounts = model.assessments
    .map((a) => ({
      ...a,
      count: model.units.filter(
        (u) => u.expected[stage.id] !== undefined && u.assessments.includes(a.id),
      ).length,
    }))
    .filter((a) => a.count > 0);

  return (
    <section
      className="study-step-surface"
      role="tabpanel"
      id={`study-step-panel-${stage.id}`}
      aria-labelledby={`study-step-tab-${stage.id}`}
    >
      <header className="study-step-intro">
        <div className="study-step-title-row">
          <h3 className="study-step-title">
            {stage.period} <span className="study-step-title-sub">{stage.label}</span>
          </h3>
          {isCurrent ? (
            <span className="study-now-badge study-now-badge--lg">いまここ</span>
          ) : onMakeCurrent ? (
            <button type="button" className="study-ghost-btn" onClick={onMakeCurrent}>
              このステップをいまここにする
            </button>
          ) : null}
        </div>
        <div className="study-step-focus">
          <span className="study-step-focus-label">このステップで大切にすること</span>
          <FocusList focus={stage.focus} />
        </div>
        {assessmentCounts.length > 0 ? (
          <div className="study-assessment-balance" data-testid={`assessment-balance-${stage.id}`}>
            <span className="study-assessment-balance-label">考査の種類</span>
            {assessmentCounts.map((a) => (
              <span
                key={a.id}
                className="study-assessment-chip"
                title={`${a.label}につながる単元が ${a.count} 個`}
              >
                <span aria-hidden="true">{ASSESSMENT_ICONS[a.id] ?? "・"}</span>
                <span className="study-assessment-chip-label">{a.label}</span>
                <span className="study-assessment-chip-count">{a.count}</span>
              </span>
            ))}
          </div>
        ) : null}
        {stage.exit.length > 0 ? (
          <div className="study-step-goals">
            <div className="study-step-goals-label">終わりまでにできていてほしいこと</div>
            <ul>
              {stage.exit.map((e) => (
                <li key={e.text}>{e.text}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {checkpoints.map((c) => (
          <details key={c.id} className="study-checkpoint">
            <summary>
              <span className="study-checkpoint-badge">{c.label}</span> {c.timing}
            </summary>
            <table className="study-kv">
              <tbody>
                <tr>
                  <th scope="row">見るもの</th>
                  <td>{c.measures}</td>
                </tr>
                <tr>
                  <th scope="row">やり方</th>
                  <td>{c.method}</td>
                </tr>
                <tr>
                  <th scope="row">目安</th>
                  <td>{c.criterion}</td>
                </tr>
                {c.notes.length > 0 ? (
                  <tr>
                    <th scope="row">補足</th>
                    <td>
                      <ul className="study-kv-notes">
                        {c.notes.map((n) => (
                          <li key={n}>{n}</li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </details>
        ))}
      </header>

      {preparationSlot}

      <div className="study-units-block">
        <h4 className="study-subsection-title">
          <span aria-hidden="true">🧭</span> 単元と取り組み
          <details className="study-legend study-legend--inline">
            <summary>「触れる」「導入」「練習」「仕上げ」の意味</summary>
            <ul>
              {model.levels.map((l) => (
                <li key={l.id}>
                  <span className={`study-level study-level--${l.id}`}>{l.label}</span>
                  {l.description}
                </li>
              ))}
            </ul>
          </details>
        </h4>
        <div className="study-domains">
          {model.domains.map((d) => {
            const units = unitsInCell(model, stage.id, d.id);
            const color = domainColor(d.id);
            return (
              <article
                key={d.id}
                className="study-domain-card"
                data-testid={`domain-${stage.id}-${d.id}`}
                style={color ? ({ "--study-domain-color": color } as CSSProperties) : undefined}
              >
                <h5 className="study-domain-title">
                  <span className="study-domain-dot" aria-hidden="true" />
                  {DOMAIN_ICONS[d.id] ? <span aria-hidden="true">{DOMAIN_ICONS[d.id]}</span> : null}
                  {d.label}
                </h5>
                <p className="study-domain-aim">{d.aim}</p>
                <ul className="study-unit-list">
                  {units.map((u) => {
                    const levelId = u.expected[stage.id];
                    const level = levelId ? levelById(model, levelId) : null;
                    const note = u.notes[stage.id];
                    const state = unitStates?.[u.id];
                    const status: StudyUnitStatus = state?.status ?? "not_started";
                    const statusLabel = STUDY_UNIT_STATUS_LABELS[status];
                    return (
                      <li key={u.id} className={`study-unit-row study-unit-row--${status}`}>
                        <div className="study-unit-main">
                          {editable ? (
                            <button
                              type="button"
                              className={`study-status-btn study-status-btn--${status}`}
                              onClick={() => onToggleUnit(u.id)}
                              aria-label={`${u.label}（${statusLabel}）`}
                              title="クリックで 未着手 → 取り組み中 → できた の順に切り替わります"
                            >
                              {statusLabel}
                            </button>
                          ) : null}
                          <span className="study-unit-label">
                            {u.label}
                            {level ? (
                              <>
                                {" "}
                                <span
                                  className={`study-level study-level--${level.id}`}
                                  title={level.description}
                                >
                                  {level.label}
                                </span>
                              </>
                            ) : null}
                            {u.assessments.map((a) => {
                              const kind = model.assessments.find((x) => x.id === a);
                              return (
                                <span
                                  key={a}
                                  className="study-assessment-icon"
                                  title={kind?.label ?? a}
                                  aria-hidden="true"
                                >
                                  {ASSESSMENT_ICONS[a] ?? "・"}
                                </span>
                              );
                            })}
                          </span>
                        </div>
                        {note ? <div className="study-unit-note">{note}</div> : null}
                        {state?.note ? (
                          <div className="study-unit-memo">メモ: {state.note}</div>
                        ) : null}
                        {(materialsByUnit?.[u.id] ?? []).length > 0 ? (
                          <div className="study-unit-materials">
                            {(materialsByUnit?.[u.id] ?? []).map((mat) => (
                              <span
                                key={mat.title}
                                className={`study-material-chip study-material-chip--${mat.status}`}
                                title={STUDY_MATERIAL_STATUS_LABELS[mat.status]}
                              >
                                {mat.title}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        <UnitCorpus
                          entries={corpusByUnit?.[u.id] ?? []}
                          unitId={u.id}
                          unitLabel={u.label}
                          open={openUnits.has(u.id)}
                          onToggle={() => toggleOpen(u.id)}
                          {...(u.basis ? { basis: u.basis } : {})}
                          {...(sourceHrefBase ? { sourceHrefBase } : {})}
                        />
                      </li>
                    );
                  })}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * 単元 1 つぶんの「実際の出題」。閉じているときは件数のボタンだけを出す。
 *
 * @what: 「計画のもと」に載っている同じ単元の出題を、出典校と図の説明で一覧する。
 *   先頭 5 件までをその場に出し、続きはリーダーで同じ単元に絞って読む。水準と時期の
 *   理由 (basis) を持つ単元は、出題の前にそれを出す。
 * @why: 単元名と水準だけでは「その水準がどのくらいか」が分からない。計画の行から
 *   実物へ 1 手で届くようにする (代表 2026-08-22)。
 * @failure: 出題が無ければ何も描かない (0 件のボタンを出さない)。
 */
function UnitCorpus({
  entries,
  unitId,
  unitLabel,
  open,
  onToggle,
  basis,
  sourceHrefBase,
}: {
  entries: CorpusEntry[];
  unitId: string;
  unitLabel: string;
  open: boolean;
  onToggle: () => void;
  basis?: string;
  sourceHrefBase?: string;
}) {
  if (entries.length === 0) return null;
  const first = entries[0]!;
  const rest = entries.length - UNIT_CORPUS_PREVIEW;
  const itemHref = (e: CorpusEntry) =>
    `${sourceHrefBase}/${encodeURIComponent(e.sourceId)}?item=${encodeURIComponent(e.item.id)}`;

  return (
    <div className="study-unit-corpus">
      <button
        type="button"
        className="study-unit-corpus-toggle"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span aria-hidden="true">{open ? "▾" : "▸"}</span> 入試での出題 {entries.length} 問
      </button>
      {open ? (
        <ul className="study-corpus-list">
          {basis ? (
            <li className="study-corpus-basis">
              <span className="study-corpus-basis-label">この時期にする理由</span>
              {basis}
            </li>
          ) : null}
          {entries.slice(0, UNIT_CORPUS_PREVIEW).map((e) => (
            <li key={`${e.sourceId}/${e.item.id}`} className="study-corpus-row">
              <span className="study-corpus-school">{e.item.index["出典校"] ?? "出典なし"}</span>
              <span className="study-corpus-summary">{entrySummary(e.item)}</span>
              {sourceHrefBase ? (
                <Link className="study-corpus-open" to={itemHref(e)}>
                  見る
                </Link>
              ) : null}
            </li>
          ))}
          {rest > 0 && sourceHrefBase ? (
            <li className="study-corpus-more">
              <Link
                to={`${sourceHrefBase}/${encodeURIComponent(first.sourceId)}?unit=${encodeURIComponent(unitId)}`}
              >
                {unitLabel} の出題を全部見る（のこり {rest} 問）
              </Link>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
